import { ref, computed, nextTick, onBeforeUnmount } from 'vue';

const SCROLL_AWAY_THRESHOLD = 120;
const TOP_PADDING = 24;
const COMPOSER_RESERVE = 140;
const RESERVE_MIN_DELTA = 6;
const RESERVE_APPLY_MS = 48;

/**
 * Claude-style turn scroll (top-anchor reserve): pin the human message near the
 * top on send, inject slack below the transcript so the assistant row can grow
 * without shifting the turn. Reserve shrinks as content grows; scroll-to-bottom
 * is suppressed for the duration. Based on assistant-ui topAnchor reserve.
 *
 * @param {import('vue').Ref<HTMLElement|null>} scrollRef
 * @param {() => HTMLElement|null} [getSizerEl] Optional sizer/trailing node so
 *   reserve updates when consent cards or tool rows change height.
 */
export function useCopilotTurnScroll(scrollRef, getSizerEl = null) {
  const isActiveTurn = ref(false);
  const sizerExcessPx = ref(0);
  const pinnedScrollTop = ref(0);
  let lastPinnedAnchorKey = null;
  let hasPinnedThisTurn = false;
  let applyRetryCount = 0;
  let scheduledFrame = null;
  let getHumanRowFn = null;
  let getAssistantRowFn = null;
  let getAnchorKeyFn = null;

  const MAX_APPLY_RETRIES = 12;

  const sizerExcessStyle = computed(() =>
    sizerExcessPx.value > 0 ? { paddingBottom: `${sizerExcessPx.value}px` } : {}
  );

  let resizeObserver = null;
  let lastReserveApplyAt = 0;

  function resolveRowEl(rowRef) {
    if (!rowRef) return null;
    if (rowRef instanceof HTMLElement) return rowRef;
    const exposed = rowRef.messageRowRef;
    if (exposed instanceof HTMLElement) return exposed;
    if (exposed?.value instanceof HTMLElement) return exposed.value;
    if (rowRef.$el instanceof HTMLElement) return rowRef.$el;
    return null;
  }

  function computeTargetScrollTop(humanRowEl) {
    const container = scrollRef.value;
    if (!container || !humanRowEl) return null;

    const containerRect = container.getBoundingClientRect();
    const rowRect = humanRowEl.getBoundingClientRect();
    return Math.max(
      0,
      container.scrollTop + (rowRect.top - containerRect.top) - TOP_PADDING
    );
  }

  function computeReserve(humanRowEl) {
    const container = scrollRef.value;
    if (!container || !humanRowEl) return 0;

    const targetScrollTop = computeTargetScrollTop(humanRowEl);
    if (targetScrollTop === null) return 0;

    const contentScrollHeight = container.scrollHeight - sizerExcessPx.value;
    const targetScrollHeight =
      targetScrollTop + container.clientHeight - COMPOSER_RESERVE;

    return Math.max(0, targetScrollHeight - contentScrollHeight);
  }

  function scrollToTurn(humanRow) {
    const container = scrollRef.value;
    const rowEl = resolveRowEl(humanRow);
    if (!container || !rowEl) {
      return false;
    }

    const target = computeTargetScrollTop(rowEl);
    if (target === null) return false;

    if (Math.abs(container.scrollTop - target) <= 1) {
      pinnedScrollTop.value = container.scrollTop;
      return true;
    }

    container.scrollTop = target;
    pinnedScrollTop.value = container.scrollTop;
    return true;
  }

  function setSizerExcess(px) {
    const next = Math.max(0, Math.round(px));
    const delta = Math.abs(next - sizerExcessPx.value);
    const now = performance.now();

    if (
      next !== 0 &&
      sizerExcessPx.value !== 0 &&
      delta < RESERVE_MIN_DELTA &&
      now - lastReserveApplyAt < RESERVE_APPLY_MS
    ) {
      return false;
    }

    if (next === sizerExcessPx.value) return false;

    lastReserveApplyAt = now;
    sizerExcessPx.value = next;
    return true;
  }

  function stopObserving() {
    resizeObserver?.disconnect();
    resizeObserver = null;
  }

  function startObserving() {
    stopObserving();

    const humanEl = resolveRowEl(getHumanRowFn?.());
    const assistantEl = resolveRowEl(getAssistantRowFn?.());
    const sizerEl = typeof getSizerEl === 'function' ? getSizerEl() : null;
    if (!humanEl && !assistantEl && !sizerEl) return;

    resizeObserver = new ResizeObserver(() => {
      scheduleApply('resize-observer');
    });

    if (humanEl) resizeObserver.observe(humanEl);
    if (assistantEl) resizeObserver.observe(assistantEl);
    if (sizerEl) resizeObserver.observe(sizerEl);
  }

  function cancelScheduledApply() {
    if (scheduledFrame === null) return;
    cancelAnimationFrame(scheduledFrame);
    scheduledFrame = null;
  }

  function applyTurnLayout(reason = 'applyTurnLayout') {
    if (!isActiveTurn.value) return;

    const human = getHumanRowFn?.();
    const humanEl = resolveRowEl(human);
    const container = scrollRef.value;

    if (!humanEl || !container) {
      if (applyRetryCount < MAX_APPLY_RETRIES) {
        applyRetryCount += 1;
        scheduleApply(`${reason}-retry`);
      }
      return;
    }

    applyRetryCount = 0;
    const anchorKey = getAnchorKeyFn?.() ?? 'active-human';

    if (!hasPinnedThisTurn) {
      scrollToTurn(human);
      lastPinnedAnchorKey = anchorKey;
      hasPinnedThisTurn = true;
    } else if (anchorKey !== lastPinnedAnchorKey) {
      scrollToTurn(human);
      lastPinnedAnchorKey = anchorKey;
    }

    const nextReserve = computeReserve(humanEl);
    const reserveChanged = setSizerExcess(nextReserve);

    if (reserveChanged) {
      scheduleApply(`${reason}-after-reserve`);
    }
  }

  function scheduleApply(reason = 'scheduleApply') {
    if (scheduledFrame !== null) return;

    scheduledFrame = requestAnimationFrame(() => {
      scheduledFrame = null;
      applyTurnLayout(reason);
    });
  }

  function afterLayout(fn) {
    nextTick(() => {
      requestAnimationFrame(fn);
    });
  }

  function beginTurn(
    getHumanRow,
    getAssistantRow,
    getAnchorKey,
    reason = 'beginTurn'
  ) {
    getHumanRowFn = getHumanRow;
    getAssistantRowFn = getAssistantRow;
    getAnchorKeyFn = getAnchorKey;
    isActiveTurn.value = true;
    lastPinnedAnchorKey = null;
    hasPinnedThisTurn = false;
    applyRetryCount = 0;

    afterLayout(() => {
      startObserving();
      scheduleApply(reason);
    });
  }

  function refreshTurn(reason = 'refreshTurn') {
    if (!isActiveTurn.value) return;
    afterLayout(() => {
      startObserving();
      scheduleApply(reason);
    });
  }

  function finishTurn(reason = 'finishTurn') {
    cancelScheduledApply();
    isActiveTurn.value = false;
    lastPinnedAnchorKey = null;
    hasPinnedThisTurn = false;
    applyRetryCount = 0;
    lastReserveApplyAt = 0;
    getHumanRowFn = null;
    getAssistantRowFn = null;
    getAnchorKeyFn = null;
    stopObserving();
    setSizerExcess(0);
  }

  function clearTurn(reason = 'clearTurn') {
    finishTurn(reason);
  }

  function handleTurnScroll() {
    const container = scrollRef.value;
    if (!container || !isActiveTurn.value) return;

    const drift = container.scrollTop - pinnedScrollTop.value;
    if (Math.abs(drift) > SCROLL_AWAY_THRESHOLD) {
      clearTurn('user-scroll-away');
    }
  }

  function pinScrollToBottom() {
    const container = scrollRef.value;
    if (!container) return;
    if (isActiveTurn.value) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  }

  onBeforeUnmount(() => {
    cancelScheduledApply();
    stopObserving();
  });

  return {
    isActiveTurn,
    sizerExcessPx,
    sizerExcessStyle,
    beginTurn,
    refreshTurn,
    finishTurn,
    clearTurn,
    handleTurnScroll,
    scrollToTurn,
    pinScrollToBottom,
  };
}

export function findLastHumanIndex(messages) {
  if (!messages?.length) return -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    // eslint-disable-next-line no-underscore-dangle
    const type = msg?.type || msg?._getType?.();
    if (type === 'human') return i;
  }
  return -1;
}

export function findLastAssistantIndex(messages) {
  if (!messages?.length) return -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    // eslint-disable-next-line no-underscore-dangle
    const type = msg?.type || msg?._getType?.();
    if (type === 'ai') return i;
  }
  return -1;
}
